import { type RefObject, useRef, useState } from 'react';

import { anchorLeft } from '../components/SelectionToolbar';
import { useSelectionStore } from '../stores/selection-store';
import { usePendingRangeHighlight } from './usePendingRangeHighlight';

interface QuotePopup {
  /** Offsets into the thread's *scrolled content*, so the panel rides along with the
   * message it points at instead of hanging at a fixed spot while the thread moves. */
  x: number;
  y: number;
  role: 'user' | 'assistant';
  snippet: string;
}

/** Enough clearance to clear the selected line rather than sit on top of it. */
const POPOVER_GAP = 6;

/** `::highlight()` name for the span the open popover is about. */
const PENDING_HIGHLIGHT = 'quote-pending';

/** Walks up from a selection endpoint to the turn that rendered it. Turns that can't be
 * quoted — the system one-liners — carry no `data-turn-role`, so they resolve to null. */
function nearestRole(node: Node | null): 'user' | 'assistant' | null {
  let el = node instanceof Element ? node : node?.parentElement;
  while (el) {
    const role = el.getAttribute?.('data-turn-role');
    if (role === 'user' || role === 'assistant') return role;
    el = el.parentElement;
  }
  return null;
}

/**
 * "Add to chat" for a span of an earlier message, the thread's counterpart to the same
 * gesture in the file viewer and the diff.
 *
 * One handler on the scroll container rather than one per turn: a selection can start in
 * one turn and end in another, and the turns themselves render through half a dozen
 * components (markdown, tool rows, reasoning) that shouldn't each grow selection plumbing.
 * Which turn it landed in is read back off the DOM instead — the endpoint's nearest
 * `data-turn-role` ancestor.
 */
export function useMessageQuote(containerRef: RefObject<HTMLDivElement | null>) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popup, setPopup] = useState<QuotePopup | null>(null);
  const addQuote = useSelectionStore((s) => s.addQuote);

  /** The span the open popover is about, kept so it can stay painted once the note field
   * takes focus and collapses the real selection. */
  const pendingRange = useRef<Range | null>(null);
  usePendingRangeHighlight(pendingRange, popup, PENDING_HIGHLIGHT);

  const onMouseUp = (e: React.MouseEvent) => {
    // Clicking into the panel — its note field included — also bubbles a mouseup up here.
    // Ignore it, or the panel unmounts out from under the click about to land on it.
    if (popoverRef.current?.contains(e.target as Node)) return;

    const selection = window.getSelection();
    const container = containerRef.current;
    if (!selection || selection.isCollapsed || selection.rangeCount === 0 || !container) {
      setPopup(null);
      return;
    }

    const range = selection.getRangeAt(0);
    const snippet = selection.toString();
    if (!container.contains(range.commonAncestorContainer) || !snippet.trim()) {
      setPopup(null);
      return;
    }

    // A selection spanning two turns is attributed to where it started — the drag's intent.
    const role = nearestRole(range.startContainer) ?? nearestRole(range.endContainer);
    if (!role) {
      setPopup(null);
      return;
    }

    // Cloned: the live range collapses along with the selection the moment focus moves.
    pendingRange.current = range.cloneRange();

    const rect = range.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    setPopup({
      x: anchorLeft(rect.left - containerRect.left + container.scrollLeft, container.clientWidth),
      y: rect.bottom - containerRect.top + container.scrollTop + POPOVER_GAP,
      role,
      snippet,
    });
  };

  const close = () => {
    pendingRange.current = null;
    setPopup(null);
  };

  const attach = (annotate: boolean) => {
    if (!popup) return;
    addQuote({ role: popup.role, snippet: popup.snippet }, annotate);
    window.getSelection()?.removeAllRanges();
    close();
  };

  return { onMouseUp, popup, popoverRef, attach, dismiss: close };
}
